import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Select,
  DatePicker,
  Button,
  Table,
  Spin,
  Empty,
  Tag,
  Badge,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import {
  useFetchSuperAdminEhrAppointments,
  useFetchSuperAdminEhrProviders,
} from 'src/hooks/ehr';
import { showToastError } from 'src/utilities/errortoast';

const EHR_SYSTEMS = [
  { value: 'advancedmd', label: 'AdvancedMD' },
  { value: 'athena', label: 'Athena' },
  { value: 'charm', label: 'Charm' },
  { value: 'office_practicum', label: 'Office Practicum' },
  { value: 'elation', label: 'Elation' },
  { value: 'nextgen', label: 'NextGen' },
  { value: 'drchrono', label: 'DrChrono' },
  { value: 'modmed', label: 'ModMed' },
];

const AppointmentLookupPage = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  // Form state for API calls
  const [searchParams, setSearchParams] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Get organizations from redux store
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );

  // Load organizations on mount
  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  // Build organization dropdown options
  const organizationOptions = useMemo(() => {
    if (
      !getActiveOrganizationsList ||
      !Array.isArray(getActiveOrganizationsList)
    ) {
      return [];
    }

    return getActiveOrganizationsList
      .filter((org) => org?.org_uuid && org?.org_name?.trim())
      .map((org) => ({
        value: org.org_uuid,
        label: org.org_name,
        org_id: org.org_id,
        ehr_system: org.ehr_system,
        practice_id: org.ehr_id,
        displayLabel: (
          <Badge
            count={org.user_count || 0}
            overflowCount={99}
            offset={[20, 7]}
            style={{ backgroundColor: '#52c41a' }}
            showZero={true}
          >
            <span className="mr-1">{org.org_name}</span>
          </Badge>
        ),
      }));
  }, [getActiveOrganizationsList]);

  // Get selected org details for EHR system and practice_id
  const selectedOrgUuid = Form.useWatch('org_uuid', form);
  const selectedEhrSystem = Form.useWatch('ehr_system', form);

  const selectedOrg = useMemo(() => {
    return organizationOptions.find((org) => org.value === selectedOrgUuid);
  }, [organizationOptions, selectedOrgUuid]);

  // Fetch providers for the selected org
  const {
    data: providersData,
    isLoading: isLoadingProviders,
    refetch: refetchProviders,
  } = useFetchSuperAdminEhrProviders(
    {
      org_uuid: selectedOrgUuid,
      ehr_system: selectedEhrSystem,
      practice_id: selectedOrg?.practice_id,
    },
    {
      enabled: Boolean(selectedOrgUuid && selectedEhrSystem),
    }
  );

  // Build provider dropdown options
  const providerOptions = useMemo(() => {
    if (!providersData?.providers || !Array.isArray(providersData.providers)) {
      return [];
    }

    return providersData.providers.map((provider) => {
      // Handle providers with profiles (AdvancedMD style)
      if (provider.profiles && provider.profiles.length > 0) {
        const firstProfile = provider.profiles[0];
        return {
          value: firstProfile.id,
          label: `${provider.name} (${firstProfile.description || firstProfile.profile_code})`,
          provider_id: firstProfile.id,
        };
      }

      return {
        value: provider.id,
        label: provider.name || `Provider ${provider.id}`,
        provider_id: provider.id,
      };
    });
  }, [providersData]);

  // Fetch appointments
  const {
    data: appointmentsData,
    isLoading: isLoadingAppointments,
    refetch: refetchAppointments,
    error: appointmentsError,
  } = useFetchSuperAdminEhrAppointments(searchParams || {}, {
    enabled: Boolean(searchParams),
  });

  // Handle search
  const handleSearch = async () => {
    try {
      const values = await form.validateFields();

      const params = {
        org_uuid: values.org_uuid,
        ehr_system: values.ehr_system,
        provider_id: String(values.provider_id),
        date: values.date.format('YYYY-MM-DD'),
        practice_id: selectedOrg?.practice_id,
      };

      setSearchParams(params);
      setIsSearching(true);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  // Reset form
  const handleReset = () => {
    form.resetFields();
    setSearchParams(null);
    setIsSearching(false);
  };

  // When org changes, auto-select its EHR system if available
  useEffect(() => {
    if (selectedOrg?.ehr_system) {
      form.setFieldsValue({ ehr_system: selectedOrg.ehr_system });
    }
  }, [selectedOrg, form]);

  // Show error toast if fetch fails
  useEffect(() => {
    if (appointmentsError) {
      showToastError('Failed to fetch appointments. Please try again.');
    }
  }, [appointmentsError]);

  // Appointments table columns
  const columns = [
    {
      title: 'Appointment ID',
      dataIndex: 'appointments_id',
      key: 'appointments_id',
      width: 120,
      render: (id) => <span className="font-mono text-xs">{id}</span>,
    },
    {
      title: 'Patient',
      key: 'patient',
      width: 200,
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.patient_name || 'N/A'}</div>
          <div className="text-xs text-gray-500">ID: {record.patient_id}</div>
        </div>
      ),
    },
    {
      title: 'Provider',
      key: 'provider',
      width: 180,
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.provider_name || 'N/A'}</div>
          <div className="text-xs text-gray-500">ID: {record.provider_id}</div>
        </div>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'appointment_start_time',
      key: 'appointment_start_time',
      width: 150,
      render: (time) => (
        <Tooltip title={time}>
          <span className="flex items-center gap-1">
            <CalendarOutlined />
            {time ? dayjs(time).format('h:mm A') : 'N/A'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'appointment_status',
      key: 'appointment_status',
      width: 120,
      render: (status) => {
        const statusColors = {
          scheduled: 'blue',
          confirmed: 'green',
          checked_in: 'cyan',
          in_progress: 'orange',
          completed: 'default',
          cancelled: 'red',
          no_show: 'red',
        };

        const statusKey = String(status).toLowerCase().replace(/\s+/g, '_');
        return (
          <Tag color={statusColors[statusKey] || 'default'}>
            {status || 'Unknown'}
          </Tag>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'appointment_type',
      key: 'appointment_type',
      width: 100,
      render: (type) => <span>{type || 'N/A'}</span>,
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 180,
      render: (_, record) => (
        <div className="text-xs">
          {record.phone && <div>📞 {record.phone}</div>}
          {record.patient_email && (
            <div className="truncate max-w-[150px]">
              ✉️ {record.patient_email}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex-1 pt-4">
      {/* Search Form */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
        <div className="text-lg font-bold font-sans mb-4 flex items-center gap-2">
          <SearchOutlined />
          EHR Appointment Lookup
        </div>
        <p className="text-gray-500 mb-4">
          Search for appointments directly from the EHR system for a specific
          organization, provider, and date.
        </p>

        <Form
          form={form}
          layout="vertical"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <Form.Item
            label="Organization"
            name="org_uuid"
            rules={[
              { required: true, message: 'Please select an organization' },
            ]}
          >
            <Select
              showSearch
              placeholder="Select organization"
              optionFilterProp="label"
              options={organizationOptions.map((org) => ({
                value: org.value,
                label: org.displayLabel,
                name: org.label,
              }))}
              filterOption={(input, option) =>
                option?.name?.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="EHR System"
            name="ehr_system"
            rules={[{ required: true, message: 'Please select an EHR system' }]}
          >
            <Select
              placeholder="Select EHR system"
              options={EHR_SYSTEMS}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Provider / Column"
            name="provider_id"
            rules={[{ required: true, message: 'Please select a provider' }]}
          >
            <Select
              showSearch
              placeholder={
                isLoadingProviders ? 'Loading providers...' : 'Select provider'
              }
              optionFilterProp="label"
              options={providerOptions}
              loading={isLoadingProviders}
              disabled={!selectedOrgUuid || !selectedEhrSystem}
              notFoundContent={
                isLoadingProviders ? (
                  <Spin size="small" />
                ) : (
                  'No providers found'
                )
              }
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Date"
            name="date"
            rules={[{ required: true, message: 'Please select a date' }]}
            initialValue={dayjs()}
          >
            <DatePicker className="w-full" format="YYYY-MM-DD" />
          </Form.Item>
        </Form>

        <div className="flex gap-3 mt-4">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={isLoadingAppointments}
            className="ih-button-primary"
          >
            Search Appointments
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            className="ih-button-secondary"
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="text-lg font-bold font-sans flex items-center gap-2">
            <CalendarOutlined />
            Appointments
            {appointmentsData?.meta?.count !== undefined && (
              <Badge
                count={appointmentsData.meta.count}
                showZero
                style={{ backgroundColor: '#1890ff' }}
              />
            )}
          </div>
          {searchParams && (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => refetchAppointments()}
              loading={isLoadingAppointments}
            >
              Refresh
            </Button>
          )}
        </div>

        {/* Search info */}
        {searchParams && appointmentsData?.meta && (
          <div className="mb-4 text-sm text-gray-500 flex flex-wrap gap-2">
            <Tag>
              <strong>EHR:</strong> {appointmentsData.meta.ehr_system}
            </Tag>
            <Tag>
              <strong>Date:</strong> {appointmentsData.meta.date}
            </Tag>
            <Tag>
              <strong>Provider ID:</strong> {appointmentsData.meta.provider_id}
            </Tag>
          </div>
        )}

        {/* Table */}
        {isSearching || searchParams ? (
          <Table
            columns={columns}
            dataSource={appointmentsData?.appointments || []}
            loading={isLoadingAppointments}
            rowKey={(record) =>
              record.appointments_id ||
              `${record.patient_id}-${record.appointment_start_time}`
            }
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} appointments`,
            }}
            scroll={{ x: 1000 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    isLoadingAppointments
                      ? 'Loading...'
                      : 'No appointments found for the selected criteria'
                  }
                />
              ),
            }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select an organization, provider, and date to search for appointments"
          >
            <div className="text-gray-400 text-sm flex items-center justify-center gap-2 mt-2">
              <UserOutlined />
              Use the form above to search
            </div>
          </Empty>
        )}
      </div>
    </div>
  );
};

export default AppointmentLookupPage;
